(() => {
  const MODAL_SELECTOR = '[data-search-modal]';
  const OPEN_SELECTOR = '[data-search-modal-open]';
  const CLOSE_SELECTOR = '[data-search-modal-close]';
  const INPUT_SELECTOR = '[data-search-modal-input]';
  const RESULTS_SELECTOR = '[data-search-modal-results]';
  const IDLE_SELECTOR = '[data-search-modal-idle]';
  const SECTION_ID = 'predictive-search';
  const DEBOUNCE_MS = 280;
  const MIN_CHARS = 1;
  const LOCAL_PRODUCT_LIMIT = 6;

  const config = () => window.atlasTheme || {};
  const routes = () => config().routes || {};
  const strings = () => config().strings || {};

  let debounceTimer = null;
  let abortController = null;
  let lastFocused = null;
  let idleMarkup = '';
  let productIndex = null;

  const getModal = () => document.querySelector(MODAL_SELECTOR);
  const getInput = (modal = getModal()) => modal?.querySelector(INPUT_SELECTOR);
  const getResults = (modal = getModal()) => modal?.querySelector(RESULTS_SELECTOR);

  const normalize = (value) =>
    String(value || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .trim();

  const loadProductIndex = () => {
    if (productIndex) return productIndex;
    const node = document.getElementById('AtlasSearchProductIndex');
    if (!node) {
      productIndex = [];
      return productIndex;
    }
    try {
      productIndex = JSON.parse(node.textContent || '[]');
    } catch (error) {
      productIndex = [];
    }
    return productIndex;
  };

  const findLocalProducts = (term) => {
    const needle = normalize(term);
    if (!needle) return [];
    return loadProductIndex()
      .filter((item) => normalize(item.keywords).includes(needle) || normalize(item.title).includes(needle))
      .slice(0, LOCAL_PRODUCT_LIMIT);
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const buildLocalProductMarkup = (products, term) => {
    if (!products.length) return '';
    const heading = strings().searchProducts || 'Products';
    const matchedLabel = strings().searchMatchedInInfo || 'Matched in product info';
    const items = products
      .map((product) => {
        const media = product.image
          ? `<img class="predictive-search__image" src="${escapeHtml(product.image)}" alt="" width="56" height="56" loading="lazy">`
          : `<span class="predictive-search__placeholder" aria-hidden="true"></span>`;
        const vendor = product.vendor
          ? `<span class="predictive-search__meta text-caption">${escapeHtml(product.vendor)}</span>`
          : '';
        return `<li class="predictive-search__item" role="option" data-local-product="${escapeHtml(product.handle)}">
          <a class="predictive-search__link predictive-search__link--product" href="${escapeHtml(product.url)}">
            <span class="predictive-search__media">${media}</span>
            <span class="predictive-search__copy">
              <span class="predictive-search__title">${escapeHtml(product.title)}</span>
              ${vendor}
              <span class="predictive-search__meta text-caption">${escapeHtml(matchedLabel)}</span>
            </span>
            <span class="predictive-search__price">${escapeHtml(product.price || '')}</span>
          </a>
        </li>`;
      })
      .join('');

    return `<div class="predictive-search__group" data-local-product-group>
      <h3 class="predictive-search__heading" id="PredictiveLocalProducts">${escapeHtml(heading)}</h3>
      <ul class="predictive-search__list" role="group" aria-labelledby="PredictiveLocalProducts">${items}</ul>
    </div>`;
  };

  const collectExistingProductHrefs = (root) => {
    const hrefs = new Set();
    root.querySelectorAll('.predictive-search__link--product[href]').forEach((link) => {
      hrefs.add(link.getAttribute('href'));
    });
    return hrefs;
  };

  const mergeLocalProducts = (resultsRoot, term) => {
    const localProducts = findLocalProducts(term);
    if (!localProducts.length) return Boolean(resultsRoot.querySelector('.predictive-search'));

    let root = resultsRoot.querySelector('.predictive-search');
    if (!root) {
      resultsRoot.innerHTML = `<div id="predictive-search-results" class="predictive-search" role="listbox"></div>`;
      root = resultsRoot.querySelector('.predictive-search');
    }

    const existing = collectExistingProductHrefs(root);
    const extras = localProducts.filter((product) => !existing.has(product.url));
    if (!extras.length) return true;

    const markup = buildLocalProductMarkup(extras, term);
    const footer = root.querySelector('.predictive-search__footer');
    const empty = root.querySelector('.predictive-search__empty');
    if (empty) empty.remove();

    if (footer) footer.insertAdjacentHTML('beforebegin', markup);
    else root.insertAdjacentHTML('afterbegin', markup);

    if (!root.querySelector('.predictive-search__footer')) {
      const viewAll = (strings().searchFor || 'View all results for “{{ terms }}”').replace('{{ terms }}', term);
      const searchUrl = `${routes().search_url || '/search'}?q=${encodeURIComponent(term)}`;
      root.insertAdjacentHTML(
        'beforeend',
        `<div class="predictive-search__footer"><a class="button predictive-search__view-all" href="${searchUrl}">${escapeHtml(viewAll)}</a></div>`
      );
    }

    return true;
  };

  const setExpanded = (expanded) => {
    const input = getInput();
    if (input) input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const showIdle = () => {
    const results = getResults();
    if (!results) return;
    results.innerHTML = idleMarkup || results.innerHTML;
    setExpanded(false);
  };

  const showLoading = () => {
    const results = getResults();
    if (!results) return;
    const loadingText = strings().searchLoading || 'Searching…';
    results.innerHTML = `<div class="search-modal__loading" data-search-modal-loading><p class="text-body-small">${loadingText}</p></div>`;
  };

  const openModal = (focus = true) => {
    const modal = getModal();
    if (!modal) return;
    lastFocused = document.activeElement;
    loadProductIndex();

    const headerDrawer = document.querySelector('[data-header-drawer]');
    if (headerDrawer && !headerDrawer.hasAttribute('hidden')) {
      headerDrawer.setAttribute('hidden', '');
      document.body.classList.remove('header-drawer-open');
      const openBtn = document.querySelector('[data-header-menu-open]');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    }

    modal.classList.add('is-open');
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-modal-open');
    if (focus) {
      const input = getInput(modal);
      if (input) {
        input.focus();
        input.select?.();
      }
    }
  };

  const closeModal = () => {
    const modal = getModal();
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-modal-open');
    setExpanded(false);
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  const buildUrl = (term) => {
    const endpoint = routes().predictive_search_url || '/search/suggest';
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product,collection,article,page,query');
    params.set('resources[limit]', '4');
    params.set('resources[limit_scope]', 'each');
    params.set('section_id', SECTION_ID);
    return `${endpoint}?${params.toString()}`;
  };

  const getSearchResults = async (term) => {
    if (abortController) abortController.abort();
    abortController = new AbortController();
    showLoading();

    try {
      const response = await fetch(buildUrl(term), {
        signal: abortController.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(String(response.status));
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section =
        doc.querySelector(`#shopify-section-${SECTION_ID}`) ||
        doc.querySelector(`#shopify-section-${SECTION_ID.replace(/_/g, '-')}`);
      const markup = section?.innerHTML?.trim();
      const results = getResults();
      if (!results) return;

      if (markup) {
        results.innerHTML = markup;
      } else {
        results.innerHTML = '';
      }

      const hasResults = mergeLocalProducts(results, term);
      if (!hasResults) {
        results.innerHTML = `<div class="predictive-search__empty"><p class="predictive-search__empty-title">${(strings().searchNoResults || 'No results for “{{ terms }}”').replace('{{ terms }}', term)}</p></div>`;
      }
      setExpanded(true);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const results = getResults();
      if (!results) return;

      results.innerHTML = '';
      const hasLocal = mergeLocalProducts(results, term);
      if (!hasLocal) {
        results.innerHTML = `<div class="predictive-search__empty"><p class="predictive-search__empty-title">${strings().searchError || 'Search failed. Please try again.'}</p></div>`;
        setExpanded(false);
      } else {
        setExpanded(true);
      }
    }
  };

  const onInput = (event) => {
    const term = event.target.value.trim();
    clearTimeout(debounceTimer);
    if (term.length < MIN_CHARS) {
      if (abortController) abortController.abort();
      showIdle();
      return;
    }
    debounceTimer = window.setTimeout(() => {
      getSearchResults(term);
    }, DEBOUNCE_MS);
  };

  const onDocumentClick = (event) => {
    const openTrigger = event.target.closest(OPEN_SELECTOR);
    if (openTrigger) {
      if (openTrigger.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
      event.preventDefault();
      openModal();
      return;
    }

    if (event.target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      closeModal();
      return;
    }

    const modal = getModal();
    if (modal && event.target === modal) closeModal();
  };

  const onKeydown = (event) => {
    const modal = getModal();
    const isOpen = modal?.classList.contains('is-open');

    if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (isOpen) closeModal();
      else openModal();
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      closeModal();
    }
  };

  const init = () => {
    const modal = getModal();
    if (!modal) return;

    const results = getResults(modal);
    const idle = results?.querySelector(IDLE_SELECTOR);
    if (idle) idleMarkup = idle.outerHTML;
    else if (results) idleMarkup = results.innerHTML;

    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    loadProductIndex();

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);

    const input = getInput(modal);
    if (input) input.addEventListener('input', onInput);

    window.AtlasSearch = {
      open: openModal,
      close: closeModal,
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
